// The navigation as server data (plan D17): `shell.nav` answers with the union of the DC-32 seat sets the person holds,
// trimmed to the items whose screen exists and whose gate tool the manifest admits, in the master order, with the
// badges, the bottom tab bar, the ? menu and the signed-in person. The browser holds no role table: it renders what
// the server sends.

import { shellNav, type NavItemOut, type ShellNavOutput } from "@/api/tools";
import { useRead } from "@/api/useTool";
import { parseHash, type Route } from "@/router";
import { useSession } from "@/session/useSession";

/** Every write invalidates this key (useWrite), so a badge or an item a write changes is current without a poll. */
export const SHELL_NAV_KEY = ["shell-nav"] as const;

export function useShellNav(options: { poll?: boolean } = {}) {
  const { me } = useSession();
  return useRead<ShellNavOutput>(SHELL_NAV_KEY, () => shellNav({}), {
    enabled: !!me,
    refetchInterval: options.poll === false ? false : 60_000,
  });
}

/** A view with no navigation item of its own and the item it sits under (UAT F-72): the admin console's pages are
 * reached from Settings, so Settings is current on `#admin/*` (an Admin item would be a new nav item). */
const UNDER_ITEM: Readonly<Record<string, string>> = { admin: "settings" };

/**
 * The item a route belongs to: the items whose route names the same view and whose query is a subset of the route's,
 * the one with the most query keys winning (Approvals is `#action-center?kind=approvals`; the Action Center item is
 * `#action-center`). A record route under a view (`#requisitions/R-1040`, `#requests/RQ-0009`) keeps its list's item
 * current. A view no item names lights the item it sits under, when the person has it.
 */
export function activeItem(items: readonly NavItemOut[], route: Route): NavItemOut | undefined {
  let best: NavItemOut | undefined;
  let bestKeys = -1;
  for (const item of items) {
    const target = parseHash(item.route);
    if (target.view !== route.view) continue;
    const wanted = [...target.params.entries()];
    if (!wanted.every(([key, value]) => (route.params.get(key) ?? "").split(",").includes(value))) continue;
    if (wanted.length > bestKeys) {
      best = item;
      bestKeys = wanted.length;
    }
  }
  if (best) return best;
  const under = UNDER_ITEM[route.view];
  return under === undefined ? undefined : items.find((item) => item.key === under);
}

/** The nav's name for a view, for the document title before the screen's own heading arrives. */
export function navTitle(nav: ShellNavOutput | undefined, view: string): string | undefined {
  const route = parseHash(view);
  return nav ? activeItem([...nav.items, ...nav.help], route)?.label : undefined;
}
